import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';
import type {
  AdminSystemUser,
  AdminUserCreateParams,
  AdminUserCreatePayload,
  AdminUserUpdateParams,
  AdminUserUpdatePayload,
  AllowedDomainsResponse,
} from '../../../../domain/src/lib/admin-users/admin-user.model';
import {
  vaultUserIsSuperadminFromSession,
  normalizeAdminUserPasswordPolicy,
  type AdminUserPasswordPolicy,
} from '@nebula/shared-vault/data-access';
import {
  resolveTenantId,
  tenantHeaders,
  withTenantParam,
} from '../shared/tenant-context.util';
import {
  extractAdminUsersArray,
  normalizeAdminSystemUser,
} from './admin-user-normalize.util';

@Injectable({ providedIn: 'root' })
export class AdminUsersService {
  private readonly http = inject(HttpClient);
  private readonly serverContext = inject(ServerContextService);
  private readonly basePath = '/api/admin/users';

  list(options?: { globalScope?: boolean }): Observable<AdminSystemUser[]> {
    /** Superadmin: listado global sin filtrar por empresa activa en sesión. */
    const globalScope = options?.globalScope ?? vaultUserIsSuperadminFromSession();
    const tenantId = globalScope ? '' : resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .get<unknown>(this.basePath, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(map((raw) => this.normalizeList(raw)));
  }

  get(id: string): Observable<AdminSystemUser> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .get<unknown>(`${this.basePath}/${id}`, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(map((raw) => this.normalizeUser(raw)));
  }

  create(
    payload: AdminUserCreatePayload,
    params?: AdminUserCreateParams,
  ): Observable<AdminSystemUser> {
    const tenantId = resolveTenantId(this.serverContext);
    let httpParams = withTenantParam(tenantId) ?? new HttpParams();
    const assignTenantId = (params?.tenant_id ?? '').trim();
    if (assignTenantId) {
      httpParams = httpParams.set('tenant_id', assignTenantId);
      const role = (params?.role ?? 'consulta').trim() || 'consulta';
      httpParams = httpParams.set('role', role);
    } else if (params?.role?.trim()) {
      httpParams = httpParams.set('role', params.role.trim());
    }
    return this.http
      .post<unknown>(this.basePath, payload, {
        headers: tenantHeaders(tenantId),
        params: httpParams,
      })
      .pipe(map((raw) => this.normalizeUser(raw)));
  }

  update(
    id: string,
    payload: AdminUserUpdatePayload,
    params?: AdminUserUpdateParams,
  ): Observable<AdminSystemUser> {
    const tenantId = resolveTenantId(this.serverContext);
    let httpParams = withTenantParam(tenantId) ?? new HttpParams();
    const body: AdminUserUpdatePayload & { tenants?: Array<{ tenant_id: string; role: string }> } = {
      ...payload,
    };
    if (params?.tenant_id) {
      httpParams = httpParams.set('tenant_id', params.tenant_id);
      body.tenant_id = params.tenant_id;
    }
    if (params?.role) {
      httpParams = httpParams.set('role', params.role);
      body.role = params.role;
    }
    if (params?.tenant_id && params?.role) {
      body.tenants = [{ tenant_id: params.tenant_id, role: params.role }];
    }
    return this.http
      .put<unknown>(`${this.basePath}/${id}`, body, {
        headers: tenantHeaders(tenantId),
        params: httpParams,
      })
      .pipe(map((raw) => this.normalizeUser(raw)));
  }

  delete(id: string): Observable<void> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http.delete<void>(`${this.basePath}/${id}`, {
      headers: tenantHeaders(tenantId),
      ...(p ? { params: p } : {}),
    });
  }

  getAllowedDomains(): Observable<AllowedDomainsResponse> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .get<unknown>(`${this.basePath}/allowed-domains`, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(
        map((raw) => {
          const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
          const domains = o['allowed_domains'];
          return {
            allowed_domains: Array.isArray(domains)
              ? domains.filter((d): d is string => typeof d === 'string')
              : [],
            is_restricted: Boolean(o['is_restricted']),
          };
        }),
      );
  }

  /** Política de contraseña efectiva (usa la empresa de la sesión). Homologación Simappe. */
  getPasswordPolicy(): Observable<AdminUserPasswordPolicy> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http
      .get<unknown>(`${this.basePath}/password-policy`, {
        headers: tenantHeaders(tenantId),
        ...(p ? { params: p } : {}),
      })
      .pipe(map((raw) => normalizeAdminUserPasswordPolicy(raw)));
  }

  private normalizeList(raw: unknown): AdminSystemUser[] {
    return extractAdminUsersArray(raw).map((u) => this.normalizeUser(u));
  }

  private normalizeUser(raw: unknown): AdminSystemUser {
    return normalizeAdminSystemUser(raw);
  }
}
